import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../../common/Logo';
import LogoutPopup from '../../Auth/LogoutPopup';
import { ChevronDown, Search, User } from '../../Helpers/icons/Icons';

type EventType = { id: number; name: string };

type PropsType = {
  isAuth: boolean;
  name?: string;
  events: EventType[];
};

/** Closes a dropdown when the pointer leaves it or Escape is pressed. */
function useDismiss(close: () => void, open: boolean) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open, close]);

  return ref;
}

const EventsMenu = ({ events }: { events: EventType[] }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(() => setOpen(false), open);

  if (!events.length) return null;

  return (
    <div ref={ref} className='relative h-full'>
      <button
        type='button'
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className='nav-link gap-1.5'
      >
        {t('event')}
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className='menu-panel absolute left-1/2 top-[calc(100%+14px)] z-50 max-h-[60vh] w-[240px] -translate-x-1/2 overflow-y-auto'>
          <p className='eyebrow eyebrow--bare px-3 pb-2 pt-2'>{t('event')}</p>
          {events.map((item) => (
            <Link
              key={item.id}
              to={`/products?events=${item.id}`}
              onClick={() => setOpen(false)}
              className='menu-item'
            >
              {item.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const AccountMenu = ({ name }: { name?: string }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const ref = useDismiss(() => setOpen(false), open);

  const items = [
    { to: '/profile', label: t('profile') },
    { to: '/profile#dashboard', label: t('sent') },
    { to: '/profile#received', label: t('received') },
    { to: '/profile#coupons', label: t('coupons') },
    { to: '/profile#events', label: t('events') },
  ];

  return (
    <>
      <div ref={ref} className='relative flex-none'>
        <button
          type='button'
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className='flex h-10 items-center gap-2 rounded-pill border border-line pl-2 pr-3 text-sm font-medium text-ink-800 transition-colors hover:border-line-strong hover:bg-canvas'
        >
          <span className='flex h-6 w-6 flex-none items-center justify-center rounded-pill bg-ink-900 text-apricot-300'>
            <User size={14} />
          </span>
          <span className='hidden max-w-[110px] truncate xl:block'>{name}</span>
          <ChevronDown size={13} className='text-ink-400' />
        </button>

        {open && (
          <div className='menu-panel absolute right-0 top-[calc(100%+10px)] z-50 w-[200px]'>
            {items.map((item) => (
              <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className='menu-item'>
                {item.label}
              </Link>
            ))}
            <div className='my-1 h-px bg-line' />
            <button
              type='button'
              onClick={() => {
                setOpen(false);
                setShowLogout(true);
              }}
              className='menu-item text-pomegranate-700'
            >
              {t('log_out')}
            </button>
          </div>
        )}
      </div>

      {showLogout && <LogoutPopup onClose={() => setShowLogout(false)} />}
    </>
  );
};

export default function MainBar({ isAuth, name, events }: PropsType) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');

  const showSearch = location.pathname !== '/products';

  const links = [
    { to: '/products', label: t('gift_cards') },
    { to: '/companies', label: t('companies') },
    { to: '/blog', label: t('blog') },
    { to: '/about', label: t('about_us') },
    { to: '/contact', label: t('contact') },
  ];

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/products?keyword=${encodeURIComponent(keyword)}`);
  };

  return (
    <div className='hidden w-full border-b border-line bg-paper xl:block'>
      <div className='container-x'>
        <div className='flex h-[76px] items-center gap-5'>
          <Logo size={40} className='flex-none' />

          <nav aria-label='Primary' className='flex h-full flex-none items-center gap-5'>
            <EventsMenu events={events} />
            {links.map((link) => (
              <NavLink key={link.to} to={link.to} className='nav-link'>
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className='flex min-w-0 flex-1 items-center justify-end gap-3'>
            {showSearch && (
              <form
                onSubmit={onSearch}
                role='search'
                className='group flex h-10 min-w-[110px] max-w-[240px] flex-1 items-center rounded-pill border border-line bg-canvas pl-3 pr-1 transition-colors duration-300 ease-gift focus-within:border-ink-900 focus-within:bg-paper'
              >
                <Search size={16} className='flex-none text-ink-400' />
                <input
                  type='search'
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder={`${t('search')}…`}
                  aria-label={`${t('search')}`}
                  className='h-full w-full min-w-0 bg-transparent px-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none'
                />
              </form>
            )}

            {isAuth ? (
              <AccountMenu name={name} />
            ) : (
              <div className='flex flex-none items-center gap-2'>
                <Link to='/login' className='btn btn-ghost btn-sm whitespace-nowrap'>
                  {t('sign_in')}
                </Link>
                <Link to='/signup' className='btn btn-ink btn-sm whitespace-nowrap'>
                  {t('sign_up')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
